// Necessary because of https://eslint.org/docs/rules/no-prototype-builtins
const hasOwnProperty = (object, prop) => ({}.hasOwnProperty.call(object, prop));

const isPositiveInteger = (n) => /^\d+$/.test(n);
const pathArrayTest = (property) => typeof property === 'string' || isPositiveInteger(property);

// Exported for the unit tests
export class InvalidPathError extends Error {
  constructor() {
    super('Invalid path array. It should only contain strings and/or positive integers');
    this.name = 'InvalidPathError';
  }
}

/**
 * This util works in a similar fashion to lodash/get and complements its `getSafe` sibling
 * with a subtle difference: the fallback value is only used _if_ the searched
 * path doesn't exist in the source object. If the searched path resolves but is falsy then this
 * falsy value will still be returned.
 *
 * E.g. for `source = { a: null }`
 * - getSafe(() => source.a) || 'fallback' === 'fallback'
 * - getOwnPath(source, ['a'], 'fallback') === null
 *
 * @param {object} object
 * @param {Array.<string|number>} pathArray
 * @param {*} [fallbackValue]
 */
export default function getOwnPath(object, pathArray, fallbackValue = undefined) {
  if (!pathArray.length) {
    return object;
  }

  if (pathArray.some((property) => !pathArrayTest(property))) {
    throw new InvalidPathError();
  }

  let currentObject = object;
  let currentPropIndex = 0;
  let currentProp = pathArray[currentPropIndex];

  while (hasOwnProperty(currentObject, currentProp)) {
    currentObject = currentObject[currentProp];

    if (currentPropIndex === pathArray.length - 1) {
      return currentObject;
    }

    currentPropIndex++;
    currentProp = pathArray[currentPropIndex];
  }

  return fallbackValue;
}
